import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  Button,
  Paper,
  Typography,
  Grid,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import Fab from '@mui/material/Fab';

// Import step components
import SelectAvatarStep from './steps/SelectAvatarStep';
import CreateLookStep from './steps/CreateLookStep';
import WriteScriptStep from './steps/WriteScriptStep';
import GenerateVideoStep from './steps/GenerateVideoStep';
import VideoConfirmationPage from './VideoConfirmationPage';


const steps = [
  'Select Avatar',
  'Create Look',
  'Write Script',
  'Generate Video',
];

function PublicAiStudio() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [activeStep, setActiveStep] = useState(0);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [selectedLook, setSelectedLook] = useState(null); // 'original', 'existing', or 'generated'
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [selectedLookId, setSelectedLookId] = useState(null); // ID for existing/generated looks
  const [selectedLookData, setSelectedLookData] = useState(null);
  const [generatedLook, setGeneratedLook] = useState(null);
  const [script, setScript] = useState('');
  const [isVideoProcessActive, setIsVideoProcessActive] = useState(false);
  const [showThankYou, setShowThankYou] = useState(false);


  const handleSelectLook = (lookType, lookData = null) => {
    setSelectedLook(lookType);
    setSelectedLookData(lookData);
    
    if (lookType === 'original') {
      setSelectedLookId('original');
    } else if (lookData) {
      setSelectedLookId(lookData.generationId);
    }
  };

  const handleNext = () => {
    if (canProceed()) {
      setActiveStep((prevStep) => prevStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleBack = () => {
    if (activeStep === 3 && isVideoProcessActive) {
      const confirmed = window.confirm(
        'If you go back now, you will lose your video progress. Do you want to continue?'
      );
      if (!confirmed) {
        return;
      }
    }

    setActiveStep((prevStep) => prevStep - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleReset = () => {
    setActiveStep(0);
    setSelectedAvatar(null);
    setSelectedLook(null);
    setSelectedLookId(null);
    setSelectedLookData(null);
    setGeneratedLook(null);
    setScript('');
    setIsVideoProcessActive(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const canProceed = () => {
    switch (activeStep) {
      case 0:
        return selectedAvatar !== null;
      case 1:
        return selectedLook !== null;
      case 2:
        return script.length >= 20 && script.length <= 1000;
      case 3:
        return false; // Last step, no next button
      default:
        return false;
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = 'Are you sure you want to leave? Your progress will be lost.';
      return event.returnValue;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    setSelectedLook(null);
    setSelectedLookId(null);
    setSelectedLookData(null);
    setGeneratedLook(null);
  }, [selectedAvatar]);

  const showThankYouOverlay = () => {
    setShowThankYou(true);
  }

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <SelectAvatarStep
            selectedAvatar={selectedAvatar}
            onSelectAvatar={setSelectedAvatar}
          />
        );
      case 1:
        return (
          <CreateLookStep
            selectedAvatar={selectedAvatar}
            selectedLook={selectedLook}
            selectedLookId={selectedLookId}
            onSelectLook={handleSelectLook}
            generatedLook={generatedLook}
            onGeneratedLook={setGeneratedLook}
          />
        );
      case 2:
        return (
          <WriteScriptStep
            selectedAvatar={selectedAvatar}
            selectedLook={selectedLook}
            selectedLookData={selectedLookData}
            generatedLook={generatedLook}
            script={script}
            onScriptChange={setScript}
          />
        );
      case 3:
        return (
          <GenerateVideoStep
            selectedAvatar={selectedAvatar}
            selectedLook={selectedLook}
            selectedLookData={selectedLookData}
            generatedLook={generatedLook}
            script={script}
            onReset={handleReset}
            onProcessActive={setIsVideoProcessActive}
            onShowThankYou={showThankYouOverlay}
          />
        );
      default:
        return 'Unknown step';
    }
  };

  return (
    <>
      {showThankYou ? (
        <VideoConfirmationPage />
      )
      :
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#fff',
          // backgroundColor: 'grey.50',
          py: { xs: 2, md: 3 },
        }}
      >
        <Container maxWidth="xxl" sx={{ px: { xs: 0, md: 3 }, borderTop: '0px solid', borderColor: '#fff' }}>
          {/* Header */}
          {/* <Box sx={{ textAlign: 'center', mb: 2, mt: { xs: 0, md: 2 } }}>
            <Typography
              variant="h3"
              component="h1"
              fontWeight={800}
              gutterBottom
              sx={{
                marginTop: { xs: 0, md: '-35px' },
                fontSize: { xs: '2rem', md: '3rem' },
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              AI Studio
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}
            >
              Create professional AI videos in minutes
            </Typography>
          </Box> */}

          {/* Stepper */}
          {/* <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              mb: 4,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Stepper
              activeStep={activeStep}
              alternativeLabel={!isMobile}
              orientation={isMobile ? 'vertical' : 'horizontal'}
              connector={
                <StepConnector
                  sx={{
                    display: isMobile ? 'none' : 'flex',
                    '& .MuiStepConnector-line': {
                      borderColor: theme.palette.divider,
                      borderTopWidth: '2px',
                    },
                    '& .MuiStepConnector-lineHorizontal': {
                      borderTopWidth: '2px',
                    },
                    '& .MuiStepConnector-lineVertical': {
                      borderLeftWidth: '2px',
                    },
                    '& .MuiStepConnector-horizontal': {
                      backgroundColor: 'theme.palette.divider',
                    },
                  }}
                />
              }
              sx={{
                '& .MuiStepLabel-label': {
                  whiteSpace: 'normal',
                },
                '& .MuiStepConnector-root': {
                  backgroundColor: 'lightgrey',
                },
              }}
            >
              {steps.map((label, index) => (
                <Step key={label} completed={activeStep > index}>
                  <StepLabel
                    sx={{
                      '& .MuiStepLabel-label': {
                        fontSize: { xs: '0.75rem', md: '0.875rem' },
                        fontWeight: activeStep === index ? 600 : 400,
                        textAlign: isMobile ? 'left' : 'center',
                      },
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Paper> */}

          {/* Main Content */}
          <Grid container spacing={4}>
            {/* Left Section - Workflow Content */}
            <Grid item xs={12}>
              <Paper
                elevation={1}
                sx={{
                  p: { xs: 2, md: 4 },
                  minHeight: 500,
                  borderRadius: 2,
                  pb: { xs: 10, md: 10 },
                }}
              >
                {getStepContent(activeStep)}
              </Paper>
            </Grid>
          </Grid>
        </Container>

        {activeStep < steps.length && (
          <Box
            sx={{
              position: 'fixed',
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1200,
              backgroundColor: 'background.paper',
              borderTop: '1px solid',
              borderColor: 'divider',
              py: 2,
              px: { xs: 2, md: 4 },
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' }, justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                startIcon={<ArrowBackIcon />}
                size="large"
                sx={{ px: 3, width: { xs: '100%', md: 'auto' } }}
              >
                Back
              </Button>
              {activeStep < steps.length - 1 ? (
                <Button
                  variant="contained"
                  onClick={handleNext}
                  disabled={!canProceed()}
                  endIcon={<ArrowForwardIcon />}
                  size="large"
                  sx={{ px: 3, width: { xs: '100%', md: 'auto' } }}
                >
                  Continue
                </Button>
              ) : null}
            </Box>
          </Box>
        )}

        {showScrollTop && (
          <Fab
            color="primary"
            size="small"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            sx={{
              position: 'fixed',
              right: 24,
              bottom: activeStep < steps.length ? 140 : 24,
              zIndex: 1300,
            }}
            aria-label="Scroll to top"
          >
            <KeyboardArrowUpIcon />
          </Fab>
        )}
      </Box>
      }
    </>
  );
}

export default PublicAiStudio;